'use client';

// Bordered "value + copy icon" field used by `<ShareModal>` for the
// "or copy link" and "or copy message" sections. Internal — not exported.
import { c as _c } from "react/compiler-runtime";
import type { useTranslations } from 'next-intl';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { Copy01Icon } from '@/components/ui/icon/glyphs';
type Translator = ReturnType<typeof useTranslations>;
export interface ShareCopyFieldProps {
  /** Section label rendered above the field (e.g. "or copy link"). */
  label: string;
  /** Glyph rendered at the start of the field (e.g. `Link01Icon`). */
  prefixIcon: typeof Copy01Icon;
  /** The value displayed in the field — URL or full message. */
  value: string;
  /** Wrapping rule. `'break-all'` truncates to a single line with an
   *  ellipsis (URLs). `'wrap-anywhere'` wraps prose+URL across lines and
   *  breaks at any character — meaning the wrap lands MID-URL once the
   *  line is full, instead of leaving the URL stranded on its own line
   *  via the prose/URL space boundary. */
  wrapMode: 'break-all' | 'wrap-anywhere';
  /** Click handler for the copy icon button. */
  onCopy: () => void;
  /** Translator scoped to `share.modal` for the post-copy "Copied!" state. */
  t: Translator;
  /** Whether the field is in its post-copy "Copied!" state. */
  copied: boolean;
  /** Distinct aria-label for the copy icon button so screen readers (and
   *  test queries) can tell the link-copy and message-copy buttons apart. */
  copyAriaLabel: string;
}
export function ShareCopyField(props) {
  const $ = _c(22);
  const wrapClass = props.wrapMode === "break-all" ? "truncate" : "break-all";
  let t0;
  if ($[0] !== props.label) {
    t0 = <p className="text-caption font-medium text-foreground-secondary">{props.label}</p>;
    $[0] = props.label;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== props.prefixIcon) {
    t1 = <Icon icon={props.prefixIcon} className="size-4 shrink-0 text-foreground-secondary" />;
    $[2] = props.prefixIcon;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = `block w-0 min-w-0 flex-1 text-caption leading-relaxed text-foreground-primary ${wrapClass}`;
  let t3;
  if ($[4] !== props.value || $[5] !== t2) {
    t3 = <span className={t2}>{props.value}</span>;
    $[4] = props.value;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== props.copied || $[8] !== props.copyAriaLabel || $[9] !== props.t) {
    t4 = props.copied ? props.t("copied") : props.copyAriaLabel;
    $[7] = props.copied;
    $[8] = props.copyAriaLabel;
    $[9] = props.t;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Icon icon={Copy01Icon} className="size-3.5" />;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== props.onCopy || $[13] !== t4) {
    t6 = <AnimatedButton type="button" variant="tertiary" size="sm" aria-label={t4} onClick={props.onCopy} className="size-7 shrink-0 p-0">{t5}</AnimatedButton>;
    $[12] = props.onCopy;
    $[13] = t4;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t1 || $[16] !== t3 || $[17] !== t6) {
    t7 = <div className="flex min-w-0 max-w-full items-center gap-2 overflow-hidden rounded-control border border-border-primary bg-background-tertiary/30 px-3 py-2">{t1}{t3}{t6}</div>;
    $[15] = t1;
    $[16] = t3;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t0 || $[20] !== t7) {
    t8 = <div className="flex min-w-0 max-w-full flex-col gap-2 overflow-hidden">{t0}{t7}</div>;
    $[19] = t0;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
}